<template>
  <v-form>
    <v-row>
      <v-col
        v-if="
          getRightDrawer.data.status.value === 'draft' &&
          getRightDrawer.data.approvalReasons?.length > 0
        "
        cols="12"
        class="pb-0"
      >
        <v-col cols="auto" class="pa-0 d-flex justify-center">
          <v-chip
            key="reasons"
            dark
            small
            color="deep-orange lighten-1"
            style="height: 100%"
          >
            <div class="d-flex flex-column align-center py-2">
              <div class="d-flex flex-row align-center">
                <v-icon small class="mr-1">mdi-information</v-icon>
                <span
                  class="font-weight-bold text-caption text-center text-uppercase"
                  >Reasons for Approval Requirement</span
                >
              </div>
              <div class="d-flex flex-column">
                <span class="d-block text-caption text-center text-wrap">{{
                  getRightDrawer.data.approvalReasons
                }}</span>
              </div>
            </div>
          </v-chip>
        </v-col>
      </v-col>
      <v-col cols="12">
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header class="px-3">
              <v-toolbar-title>Line Items</v-toolbar-title>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-data-table
                :headers="response.headers.lineItems"
                :items="groupedLineItems"
                hide-default-footer
                group-by="categoryName"
                mobile-breakpoint="0"
                :items-per-page="-1"
              >
                <template
                  v-slot:group.header="{
                    group,
                    items,
                    isOpen,
                    toggle,
                    remove,
                    headers,
                  }"
                >
                  <GroupByHeaderRow
                    :headers="headers"
                    :group="group"
                    :items="items"
                    :isOpen="isOpen"
                    :showRemoveIcon="false"
                    @toggle="toggle"
                    @remove="remove"
                  />
                </template>
                <template v-slot:item.number="{ item }">
                  <span>{{
                    groupedLineItems.findIndex(
                      (lineItem) =>
                        (lineItem.id && lineItem.id === item.id) ||
                        lineItem.sku.toLowerCase() === item.sku.toLowerCase()
                    ) + 1
                  }}</span>
                </template>
                <template v-slot:item.name="{ item }">
                  <span class="text-capitalize font-weight-bold d-block">{{
                    item.name
                  }}</span>
                  <span
                    class="text-uppercase text-caption text-no-wrap d-block"
                    >{{ item.sku }}</span
                  >
                  <span
                    v-if="item.promo"
                    class="text-caption text-no-wrap d-block my-1"
                  >
                    <span
                      class="font-weight-bold text-caption pa-1 rounded"
                      :style="`background-color: ${
                        $vuetify.theme.currentTheme.promoUnit
                      }; color: ${getTextColor(
                        $vuetify.theme.currentTheme.promoUnit
                      )}`"
                      >Promo
                      <v-icon
                        small
                        :style="`color: ${getTextColor(
                          $vuetify.theme.currentTheme.promoUnit
                        )}`"
                        >mdi-seal</v-icon
                      >
                    </span>
                  </span>
                  <span
                    v-if="getRightDrawer.data.sample"
                    class="text-caption text-no-wrap d-block my-1"
                  >
                    <span
                      class="font-weight-bold text-caption pa-1 rounded"
                      :style="`background-color: ${$vuetify.theme.currentTheme.review}; color: white`"
                      >Sample
                    </span>
                  </span>
                  <span
                    v-if="item.flags.fulfilled || item.flags.delivered"
                    class="text-caption text-no-wrap d-block my-1"
                  >
                    <span
                      class="font-weight-bold text-caption pa-1 rounded"
                      :style="`background-color: ${
                        item.flags.delivered
                          ? $vuetify.theme.currentTheme.shipping
                          : $vuetify.theme.currentTheme.products
                      }; color: ${getTextColor(
                        item.flags.fulfilled
                          ? $vuetify.theme.currentTheme.shipping
                          : $vuetify.theme.currentTheme.products
                      )}`"
                      >{{
                        item.flags.delivered ? "Delivered" : "Fulfilled"
                      }}</span
                    >
                  </span>
                </template>
                <template v-slot:item.sku="{ item }">
                  <span class="text-uppercase">{{ item.sku }}</span>
                </template>
                <template v-slot:item.categoryName="{ item }">
                  <span class="text-capitalize">{{ item.categoryName }}</span>
                </template>
                <template v-slot:item.sample="{ item }">
                  <template v-if="item.sample">
                    <v-icon :color="$vuetify.theme.currentTheme.review"
                      >mdi-alpha-s-circle
                    </v-icon>
                  </template>
                  <template v-else>
                    <span class="text--disabled">-</span>
                  </template>
                </template>
                <template v-slot:item.price="{ item }">
                  {{ item.amount | currency }}
                </template>
                <template v-slot:item.totalUnits="{ item }">
                  <span class="font-weight-bold">{{
                    item.qty | addComma
                  }}</span>
                </template>
                <template v-slot:item.total="{ item }">
                  <span>{{ (item.amount * item.qty) | currency }}</span>
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
      <v-col cols="12" md="12" sm="12">
        <v-btn
          block
          rounded
          large
          :style="`background: ${$vuetify.theme.currentTheme.review}; color: ${$vuetify.theme.currentTheme.secondary};`"
          :loading="loading.invoice"
          @click="generateInvoice"
        >
          <v-icon class="mr-1">mdi-download</v-icon>
          <span>download order</span>
        </v-btn>
      </v-col>
      <v-col cols="6" md="6">
        <v-text-field
          label="Total Units"
          :color="$vuetify.theme.currentTheme.primary"
          :value="getRightDrawer.data.units | addComma"
          hide-details
          readonly
        >
        </v-text-field>
      </v-col>
      <v-col cols="6" md="6" sm="12">
        <v-text-field
          label="Total Sum"
          :color="$vuetify.theme.currentTheme.primary"
          :value="getRightDrawer.data.total | currency"
          readonly
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="6" md="6">
        <v-text-field
          label="Promo Units"
          :color="$vuetify.theme.currentTheme.primary"
          :background-color="
            promoUnitQty === 0 ? undefined : 'purple lighten-4'
          "
          :value="promoUnitQty | addComma"
          hide-details
          readonly
          :disabled="promoUnitQty === 0"
        >
        </v-text-field>
      </v-col>
      <v-col cols="6" md="6" sm="12">
        <v-text-field
          label="Promo Unit Value"
          :color="$vuetify.theme.currentTheme.primary"
          :value="promoUnitValue | currency"
          readonly
          :hint="`Percentage: ${Number(
            parseFloat(promoUnitPercentage).toFixed(2)
          )}%`"
          persistent-hint
          :disabled="promoUnitQty === 0"
        ></v-text-field>
      </v-col>
      <v-col cols="6" md="6">
        <v-text-field
          label="Status"
          :color="$vuetify.theme.currentTheme.primary"
          :value="getRightDrawer.data.status.text"
          hide-details
          readonly
        >
          <template v-slot:prepend-inner>
            <v-badge
              inline
              dot
              :color="getRightDrawer.data.status.color"
            ></v-badge>
          </template>
        </v-text-field>
      </v-col>
      <v-col
        cols="6"
        :md="
          !['draftOrder', 'mytrace_draft'].includes(
            getRightDrawer.data.status.value
          )
            ? 3
            : 6
        "
        sm="12"
      >
        <v-text-field
          label="Payment Term"
          :color="$vuetify.theme.currentTheme.primary"
          :value="getRightDrawer.data.paymentTermDef.text"
          readonly
          hide-details
        ></v-text-field>
      </v-col>
      <v-col
        v-if="
          !['draftOrder', 'mytrace_draft'].includes(
            getRightDrawer.data.status.value
          )
        "
        cols="6"
        md="3"
        sm="12"
      >
        <v-text-field
          label="Ship Date"
          :color="$vuetify.theme.currentTheme.primary"
          :value="
            getRightDrawer.data.shipDate
              ? $options.filters.moment(
                  getRightDrawer.data.shipDate,
                  'MM/DD/YYYY'
                )
              : null
          "
          readonly
          hide-details
          :disabled="!getRightDrawer.data.shipDate"
        ></v-text-field>
      </v-col>
      <v-col cols="6" md="6" sm="12">
        <v-text-field
          label="Internal Order #"
          :color="$vuetify.theme.currentTheme.primary"
          :value="
            getRightDrawer.data.orderNumber.internal
              ? String(getRightDrawer.data.orderNumber.internal).toUpperCase()
              : '-'
          "
          readonly
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6" sm="12">
        <v-text-field
          label="External Order #"
          :color="$vuetify.theme.currentTheme.primary"
          :value="
            getRightDrawer.data.orderNumber.external?.length > 0
              ? getRightDrawer.data.orderNumber.external
              : '-'
          "
          readonly
          :disabled="getRightDrawer.data.orderNumber.external?.length === 0"
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="12" sm="12">
        <v-text-field
          label="Account"
          :color="$vuetify.theme.currentTheme.primary"
          :value="getRightDrawer.data.accountData?.name | capitalize"
          hide-details
          readonly
        >
          <template v-slot:append-outer>
            <v-btn
              small
              rounded
              :style="`background: ${$vuetify.theme.currentTheme.review}; color: ${$vuetify.theme.currentTheme.secondary};`"
              @click="
                traverse('account-view-newTab', {
                  id: getRightDrawer.data.accountID,
                })
              "
            >
              <span class="mr-1">view</span>
              <v-icon>{{ $vuetify.icons.values.view }}</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="6" md="6" sm="12">
        <v-text-field
          label="Account Balance"
          :color="$vuetify.theme.currentTheme.primary"
          :value="
            $options.filters.currency(
              this.getRightDrawer.data.accountData.balance
            )
          "
          readonly
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="6" md="6" sm="12">
        <v-text-field
          label="Account Past Due Balance"
          :color="$vuetify.theme.currentTheme.primary"
          :background-color="
            getRightDrawer.data.accountData.pastDueBalance === 0
              ? undefined
              : 'red lighten-4'
          "
          :value="
            $options.filters.currency(
              this.getRightDrawer.data.accountData.pastDueBalance ?? 0
            )
          "
          readonly
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="12" sm="12">
        <v-text-field
          label="License Number"
          :background-color="licenseExpired ? 'red lighten-3' : ''"
          :color="$vuetify.theme.currentTheme.primary"
          :value="getRightDrawer.data.accountData.license.number.toUpperCase()"
          :hint="`Expires: ${
            getRightDrawer.data.accountData.license?.expiration
              ? $options.filters.moment(
                  getRightDrawer.data.accountData.license.expiration.seconds *
                    1000,
                  'MM/DD/YYYY'
                )
              : '-'
          }`"
          persistent-hint
          readonly
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="12" sm="12">
        <v-text-field
          label="Phone Number"
          :color="$vuetify.theme.currentTheme.primary"
          readonly
          :value="getRightDrawer.data.accountData.phoneNumber | phoneNumber"
          hide-details
        >
          <template v-slot:append-outer>
            <v-btn
              v-if="getRightDrawer.data.accountData.phoneNumber?.length > 0"
              small
              rounded
              :style="`background: ${$vuetify.theme.currentTheme.review}; color: ${$vuetify.theme.currentTheme.secondary};`"
              @click="
                viewResource(
                  'phone',
                  getRightDrawer.data.accountData.phoneNumber
                )
              "
            >
              <span class="mr-1">call</span>
              <v-icon>{{ $vuetify.icons.values.phoneNumber }}</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="12" sm="12">
        <v-text-field
          label="Email"
          hide-details
          :color="$vuetify.theme.currentTheme.primary"
          :value="getRightDrawer.data.accountData.email"
          readonly
          :disabled="getRightDrawer.data.accountData.email.length === 0"
        >
          <template v-slot:append-outer>
            <v-btn
              v-if="getRightDrawer.data.accountData.email.length > 0"
              small
              rounded
              :style="`background: ${$vuetify.theme.currentTheme.review}; color: ${$vuetify.theme.currentTheme.secondary};`"
              @click="
                viewResource('email', getRightDrawer.data.accountData.email)
              "
            >
              <span class="mr-1">email</span>
              <v-icon>{{ $vuetify.icons.values.email }}</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="12" sm="12">
        <v-text-field
          label="Address"
          :color="$vuetify.theme.currentTheme.primary"
          :value="deliveryAddress"
          readonly
          hide-details
        >
          <template v-slot:append-outer>
            <v-btn
              small
              rounded
              :style="`background: ${$vuetify.theme.currentTheme.review}; color: ${$vuetify.theme.currentTheme.secondary};`"
              @click="loadAddress"
            >
              <span class="mr-1">map</span>
              <v-icon>{{ $vuetify.icons.values.map }}</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="12" sm="12">
        <v-textarea
          label="Order Notes"
          :color="$vuetify.theme.currentTheme.primary"
          outlined
          :value="getRightDrawer.data.notes"
          readonly
          hide-details
          :disabled="getRightDrawer.data.notes.length === 0"
        ></v-textarea>
      </v-col>
      <v-col cols="12" md="6" sm="12">
        <v-textarea
          label="Internal Notes"
          :color="$vuetify.theme.currentTheme.primary"
          outlined
          :value="getRightDrawer.data.internalNotes"
          readonly
          hide-details
          :disabled="getRightDrawer.data.internalNotes.length === 0"
        ></v-textarea>
      </v-col>
      <v-col cols="12" md="6" sm="12">
        <v-textarea
          label="Delivery Preferences"
          outlined
          hide-details
          background-color="optional"
          :color="$vuetify.theme.currentTheme.primary"
          :value="getRightDrawer.data.deliveryPreferences"
          readonly
          :disabled="
            getRightDrawer.data.deliveryPreferences &&
            getRightDrawer.data.deliveryPreferences.length === 0
          "
        ></v-textarea>
      </v-col>
      <v-col cols="6" md="6">
        <v-text-field
          label="Created"
          :color="$vuetify.theme.currentTheme.primary"
          :value="getRightDrawer.data.created | moment('MM/DD/YYYY @ hh:mm A')"
          persistent-hint
          :hint="`created by: ${getRightDrawer.data?.createdBy}` | capitalize"
          readonly
        >
        </v-text-field>
      </v-col>
      <v-col cols="6" md="6" sm="12">
        <v-text-field
          label="Updated"
          :color="$vuetify.theme.currentTheme.primary"
          :value="getRightDrawer.data.updated | moment('MM/DD/YYYY @ hh:mm A')"
          persistent-hint
          :hint="
            `last updated: ${getRightDrawer.data?.lastUpdated}` | capitalize
          "
          readonly
        ></v-text-field>
      </v-col>
      <template
        v-if="
          getRightDrawer.data.status.value === 'draft' &&
          getUser.accountType >= 3
        "
      >
        <v-col cols="12" class="d-flex justify-space-around">
          <v-col cols="6" class="pa-0 pr-1">
            <v-btn
              block
              rounded
              large
              :disabled="loading.submit || rejectionMenuActive"
              :loading="loading.approveOrder"
              :style="`background: ${$vuetify.theme.currentTheme.success}; color: ${$vuetify.theme.currentTheme.secondary};`"
              @click="approveOrder"
            >
              <span class="mr-1">approve</span>
              <v-icon>{{ $vuetify.icons.values.submit }}</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="6" class="pa-0 pl-1">
            <v-container fluid class="pa-0">
              <v-menu
                top
                class="rounded-lg"
                offset-y
                :close-on-content-click="false"
                ref="rejectionMenu"
                v-model="rejectionMenuActive"
              >
                <template v-slot:activator="{ on, props }">
                  <v-btn
                    v-on="on"
                    v-bind="props"
                    block
                    rounded
                    large
                    :disabled="
                      loading.submit ||
                      loading.approveOrder ||
                      rejectionMenuActive
                    "
                    :style="`background: ${$vuetify.theme.currentTheme.error}; color: ${$vuetify.theme.currentTheme.secondary};`"
                  >
                    <span class="mr-1">reject</span>
                    <v-icon>{{ $vuetify.icons.values.cancel }}</v-icon>
                  </v-btn>
                </template>
                <v-card class="rounded-lg">
                  <v-card-title>
                    <span class="text-capitalize">rejection notes</span>
                  </v-card-title>
                  <v-card-text>
                    <v-textarea
                      outlined
                      hide-details
                      :background-color="
                        rejectionNotes.length > 0 ? '' : 'required'
                      "
                      :color="$vuetify.theme.currentTheme.primary"
                      v-model.trim="rejectionNotes"
                      label="Reason for rejection"
                    ></v-textarea>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn
                      small
                      rounded
                      :style="`background: ${$vuetify.theme.currentTheme.error}; color: ${$vuetify.theme.currentTheme.secondary};`"
                      @click="
                        $refs.rejectionMenu.save();
                        rejectionNotes = '';
                      "
                      :disabled="loading.submit"
                    >
                      <v-icon small class="mr-1">{{
                        $vuetify.icons.values.cancel
                      }}</v-icon>
                      <span>cancel</span>
                    </v-btn>
                    <v-spacer />
                    <v-btn
                      small
                      rounded
                      :style="`background: ${$vuetify.theme.currentTheme.success}; color: ${$vuetify.theme.currentTheme.secondary};`"
                      :loading="loading.submit"
                      @click="rejectOrder"
                      :disabled="rejectionNotes.length === 0"
                    >
                      <span class="mr-1">submit</span>
                      <v-icon small>{{ $vuetify.icons.values.submit }}</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-container>
          </v-col>
        </v-col>
      </template>
    </v-row>
  </v-form>
</template>

<script>
// Libraries
import { mapActions, mapGetters } from "vuex";
import {
  // Firestore
  firestore,
  collection,
  onSnapshot,
  getDoc,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  writeBatch,
  or,
  increment,
  limit,
  // Functions
  functions,
  httpsCallable,
  // Storage
  storage,
  ref,
  getDownloadURL,
  getMetadata,
  getBytes,
  uploadBytes,
} from "@/firebase/init";
import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Buffer } from "buffer";
import SalesOrderClient from "@/utils/SalesOrderClient.js";

export default {
  name: "SalesOrderPreview",
  data: () => ({
    loading: {
      submit: false,
      approveOrder: false,
      invoice: false,
    },
    response: {
      headers: {
        lineItems: [
          { text: "#", value: "number", sortable: false, groupable: false },
          { text: "Name", value: "name", sortable: false, groupable: false },
          { text: "Price", value: "price", sortable: false, groupable: false },
          {
            text: "Units",
            value: "totalUnits",
            sortable: false,
            groupable: false,
            align: "center",
          },
          {
            text: "Total",
            value: "total",
            sortable: false,
            groupable: false,
            align: "center",
          },
        ],
      },
      payableTo: null,
      brand: null,
      ordersToCollect: [],
      brandMetrics: null,
      lineItems: [],
      payments: [],
      ordersForPreviousAccountBalance: [],
    },
    rejectionNotes: "",
    rejectionMenuActive: false,
  }),
  methods: {
    ...mapActions(["_rightDrawer"]),
    /* API */
    // GET
    // POST
    async approveOrder() {
      this.loading.approveOrder = true;

      try {
        let salesOrderClient = new SalesOrderClient(this.rightDrawer.data.id);
        // Wait for order and line items to load before calling approvalAction
        await salesOrderClient.getOrder();
        await salesOrderClient.getLineItems();
        await salesOrderClient.approvalAction(true, "");

        this._rightDrawer({
          active: false,
          title: null,
          type: null,
          data: {},
        });
        this.$emit("success-snackbar", {
          active: true,
          msg: "Successfully approved order!",
        });
        this.rejectionMenuActive = false;
      } catch (error) {
        console.error("Error approving order:", error);
        this.$emit("success-snackbar", {
          active: true,
          msg: "Error approving order. Please try again.",
          color: "error",
        });
      } finally {
        this.loading.approveOrder = false;
      }
    },
    async rejectOrder() {
      this.loading.submit = true;

      const batch = writeBatch(firestore);

      const stateDoc = doc(
        firestore,
        "states",
        this.rightDrawer.data.filters.state
      );
      const stateResponse = await getDoc(stateDoc);

      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.rightDrawer.data.filters.brand}`
      );

      let order = this.rightDrawer.data;

      const orderDoc = doc(firestore, "salesOrders", order.id);

      const oldValue = this.getOrderStatus.find(
          (status) => status.value === order.status.value
        ),
        newValue = this.getOrderStatus.find(
          (status) => status.value === "cancelled"
        );

      // Update order
      batch.set(
        orderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: this.getUser.name,
            uid: this.getUser.uid,
          },
          status: newValue.value,
          records: {
            cancelled: moment().startOf("day").add(12, "hours").toDate(),
          },
        },
        { merge: true }
      );

      let orderIsExternal = (order) => {
        return order.flags?.external;
      };

      let orderIsLeaflinkOrder = (order) => {
        return order.externalIDs?.leaflink?.length > 0;
      };

      let externalOrderType = (order) => {
        if (orderIsExternal(order)) {
          if (orderIsLeaflinkOrder(order)) {
            return "leaflink";
          }
        }
        return null;
      };

      if (orderIsExternal(order)) {
        switch (externalOrderType(order)) {
          case "leaflink":
            await this.transitionLeaflinkOrder("cancel", order);
            break;
          default:
            break;
        }
      }

      order.lineItems.forEach((item) => {
        batch.set(
          locationRef,
          {
            manufacturing: {
              [item.categoryID]: {
                reserved: {
                  [item.productID]: increment(-item.qty),
                },
              },
            },
          },
          { merge: true }
        );
      });

      let updateString = `<span class="text-uppercase">rejected</span>`;
      let rejectionNotes =
        this.rejectionNotes.length > 0
          ? ` | Notes: ${this.rejectionNotes}`
          : "";

      // Ledger
      batch.set(
        doc(collection(firestore, "ledger")),
        {
          created: serverTimestamp(),
          date: serverTimestamp(),
          relatedID: order.id,
          relatedTo: "salesOrders",
          action: `<span class="text-capitalize">${this.getUser.name.first} ${this.getUser.name.last}</span> ${updateString} Sales Order | <span class="text-capitalize">status</span> - <span class="text-uppercase">old</span>: <span class="font-weight-bold" style="color: ${oldValue.color};">${oldValue.text}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold" style="color: ${newValue.color};">${newValue.text}</span>${rejectionNotes}`,
          user: {
            name: this.getUser.name,
            uid: this.getUser.uid,
          },
        },
        { merge: true }
      );

      await batch.commit();
      this.loading.submit = false;
      this.$refs.rejectionMenu.save();
      this.rejectionNotes = "";

      const notificationsRef = collection(firestore, "notifications");
      const related = where("relatedTo.id", "==", this.rightDrawer.data.id);
      const notes = where("notes", "==", "Order Approval Required");
      const viewed = where("viewed", "==", false);

      const q = query(notificationsRef, related, notes, viewed);

      const response = await getDocs(q);

      if (!response.empty) {
        await updateDoc(
          doc(firestore, "notifications", response.docs[0].id),
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: this.getUser.name,
              uid: this.getUser.uid,
            },
            viewed: true,
          },
          { merge: true }
        );
      }

      this._rightDrawer({
        active: false,
        title: null,
        type: null,
        data: {},
      });

      this.$emit("success-snackbar", {
        active: true,
        msg: "Successfully rejected order!",
      });

      this.rejectionMenuActive = false;
    },
    async transitionLeaflinkOrder(transition, order) {
      console.log("Transition Leaflink Order status:", transition);
      try {
        const updateOrderStatus = await httpsCallable(functions, "externalAPI");
        const { data } = await updateOrderStatus({
          method: "POST",
          headers: {
            Authorization: `App ${this.rightDrawer.data.payableTo.leaflink.api}`,
          },
          url: `${process.env.VUE_APP_LEAFLINK}api/v2/orders-received/${order.externalIDs.leaflink}/transition/${transition}/`,
        });
      } catch (e) {
        console.log(e);
        throw new Error(e);
      }
    },
    /* Main */
    async traverse(action, params) {
      let link;
      switch (action) {
        case "account-view-newTab":
          link = this.$router.resolve({
            name: "accounts-view",
            params: {
              id: params.id,
            },
          });
          window.open(link.href, "_blank");
          break;
      }
    },
    async getPayableTo() {
      let order = this.rightDrawer.data;
      const payableToRef = collection(firestore, "payableTo");
      const state = where(
          "location.state.stateID",
          "==",
          order.location.shipping.state.stateID
        ),
        active = where("active", "==", true),
        brand = where("brandID", "==", order.brandID || order.brand.id);
      const q = query(payableToRef, state, brand, active);
      const response = await getDocs(q, limit(1));

      this.response.payableTo = response.docs[0].data();
    },
    async getBrand() {
      let order = this.rightDrawer.data;
      const brandDoc = doc(
        firestore,
        "companyBrands",
        order.brandID || order.brand.id
      );
      const response = await getDoc(brandDoc);

      if (response.exists()) {
        this.response.brand = response.data();
      }
    },
    async getOrdersToCollect() {
      let order = this.rightDrawer.data;
      this.response.ordersToCollect = [];

      const ordersDoc = collection(firestore, "salesOrders");
      const relatedAccount = where("account.id", "==", order.accountID),
        relatedBrand = where("brand.id", "==", order.brandID || order.brand.id),
        status = where("status", "==", "shipped");
      const q = query(ordersDoc, relatedAccount, relatedBrand, status);

      onSnapshot(q, (response) => {
        if (!response.empty) {
          this.response.ordersToCollect = response.docs
            .filter(
              (doc) =>
                Number(parseFloat(doc.data().balance.due).toFixed(2)) > 0 &&
                doc.id !== order.id
            )
            .map((salesOrder) => {
              const status = this.getOrderStatus.find(
                (status) => status.value === salesOrder.data().status
              );

              const term = this.getPaymentTerms.find(
                (paymentTerm) =>
                  paymentTerm.value === salesOrder.data().paymentTerm
              );

              var paymentDueDate = moment(
                salesOrder.data().shipDate?.seconds * 1000
              )
                .add(term.due, "days")
                .toDate();

              return {
                id: salesOrder.id,
                created: moment(
                  salesOrder.data().created?.seconds * 1000
                ).toDate(),
                updated: moment(
                  salesOrder.data().updated?.seconds * 1000
                ).toDate(),
                internal: String(salesOrder.data().orderNumber.internal),
                external: salesOrder.data().orderNumber.external,
                createdBy: `${salesOrder.data().createdBy.name.first} ${
                  salesOrder.data().createdBy.name.last
                }`,
                balance: Number(salesOrder.data().balance.due),
                total: Number(salesOrder.data().balance.total),
                status,
                shipDate: moment(
                  salesOrder.data().shipDate?.seconds * 1000
                ).toDate(),
                units: salesOrder.data().units,
                account: salesOrder.data().account.name,
                paymentTerm: salesOrder.data().paymentTerm,
                paymentDueDate: paymentDueDate,
              };
            })
            .sort((a, b) => (a.created > b.created ? 1 : -1))
            .filter((order) => {
              const term = this.getPaymentTerms.find(
                (paymentTerm) => paymentTerm.value === order.paymentTerm
              );
              var shouldCollect =
                moment(order.shipDate).add(term.due, "days").toDate() <=
                moment().toDate();
              return shouldCollect;
            });
        } else {
          this.response.ordersToCollect = [];
        }
      });
    },
    async getOrdersForPreviousAccountBalance() {
      let order = this.rightDrawer.data;
      this.response.ordersForPreviousAccountBalance = [];

      const ordersDoc = collection(firestore, "salesOrders");
      const relatedAccount = where("account.id", "==", order.accountID),
        relatedBrand = where("brand.id", "==", order.brandID || order.brand.id),
        status = where("status", "in", ["shipped", "complete"]);
      const q = query(ordersDoc, relatedAccount, relatedBrand, status);

      const response = await getDocs(q);

      if (!response.empty) {
        this.response.ordersForPreviousAccountBalance = response.docs
          .filter(
            (doc) =>
              Number(parseFloat(doc.data().balance.due).toFixed(2)) > 0 &&
              doc.id !== order.id
          )
          .map((salesOrder) => {
            const status = this.getOrderStatus.find(
              (status) => status.value === salesOrder.data().status
            );

            const term = this.getPaymentTerms.find(
              (paymentTerm) =>
                paymentTerm.value === salesOrder.data().paymentTerm
            );

            var paymentDueDate = moment(
              salesOrder.data().shipDate?.seconds * 1000
            )
              .add(term.due, "days")
              .toDate();

            return {
              id: salesOrder.id,
              created: moment(
                salesOrder.data().created?.seconds * 1000
              ).toDate(),
              updated: moment(
                salesOrder.data().updated?.seconds * 1000
              ).toDate(),
              internal: `${salesOrder.data().account.name} - #${
                salesOrder.data().orderNumber.internal
              }`,
              external: salesOrder.data().orderNumber.external,
              createdBy: `${salesOrder.data().createdBy.name.first} ${
                salesOrder.data().createdBy.name.last
              }`,
              balance: Number(salesOrder.data().balance.due),
              total: Number(salesOrder.data().balance.total),
              status,
              shipDate: moment(
                salesOrder.data().shipDate?.seconds * 1000
              ).toDate(),
              units: salesOrder.data().units,
              account: salesOrder.data().account.name,
              paymentTerm: salesOrder.data().paymentTerm,
              paymentDueDate: paymentDueDate,
            };
          })
          .sort((a, b) => (a.created > b.created ? 1 : -1))
          .filter((order) => {
            const term = this.getPaymentTerms.find(
              (paymentTerm) => paymentTerm.value === order.paymentTerm
            );
            var shouldCollect =
              moment(order.shipDate).add(term.due, "days").toDate() <=
              moment().toDate();
            return shouldCollect;
          });
      } else {
        this.response.ordersForPreviousAccountBalance = [];
      }
    },
    async getBrandMetrics() {
      let order = this.rightDrawer.data;
      this.response.brandMetrics = null;

      const accountDoc = doc(
        firestore,
        "brandStats",
        `${order.accountData.id}_${order.brandID || order.brand.id}`
      );
      const response = await getDoc(accountDoc);

      if (response.exists()) {
        this.response.brandMetrics = {
          ...response.data(),
          paymentTerms: response.data().paymentTerms || "[Not Set]",
        };
        if (
          response.data().paymentTerms &&
          this.getPaymentTerms.find(
            (item) => item.value === response.data().paymentTerms
          )
        ) {
          this.response.brandMetrics.paymentTerms = this.getPaymentTerms.find(
            (item) => item.value === response.data().paymentTerms
          ).text;
        }
      }
    },
    async getLineItems() {
      let order = this.rightDrawer.data;
      this.response.lineItems = [];

      const lineItems = collection(firestore, "salesOrders_lineItems");
      const related = where("relatedID", "==", order.id);
      const q = query(lineItems, related);
      const response = await getDocs(q);

      if (!response.empty) {
        this.response.lineItems = response.docs
          .map((lineItem) => {
            return {
              ...lineItem.data(),
              id: lineItem.id,
              notes: lineItem.data().notes || "",
            };
          })
          .sort((a, b) => (a.sku > b.sku ? 1 : -1));
      }
    },
    async getPayments() {
      this.response.payments = [];

      const paymentsRef = collection(firestore, "salesOrders_payments");
      const related = where("relatedID", "==", this.rightDrawer.data.id);
      const q = query(paymentsRef, related);
      const response = await getDocs(q);

      if (!response.empty) {
        let promises = response.docs.map(async (payment) => {
          const paymentType = this.getPaymentTypes.find(
            (type) => type.value === payment.data().paymentType
          );

          let promotionCredit = null;

          if (payment.data().promotionCredit) {
            promotionCredit = await getDoc(
              doc(firestore, "promotionCredits", payment.data().promotionCredit)
            );
          }

          return {
            id: payment.id,
            paymentType: paymentType.text,
            fullPaymentType: paymentType,
            paymentDate: moment(
              payment.data().paymentDate?.seconds * 1000
            ).toDate(),
            amount: payment.data().amount,
            checkNumber: payment.data().checkNumber,
            notes: payment.data().notes ?? "",
            createdBy: `${payment.data().createdBy.name.first} ${
              payment.data().createdBy.name.last
            }`,
            fullCreatedBy: payment.data().createdBy,
            flags: payment.data().flags || null,
            parentPaymentID: payment.data().parentPaymentID || null,
            creditsApplied: payment.data().creditsApplied || 0,
            overpaymentCreditID: payment.data().overpaymentCreditID ?? null,
            promotionCredit: promotionCredit?.data()
              ? {
                  id: promotionCredit.id,
                  ...promotionCredit.data(),
                  type: this.getCreditTypes.find(
                    (item) =>
                      item.value === promotionCredit.data().type ?? "other"
                  ).text,
                }
              : null,
          };
        });

        this.response.payments = await Promise.all(promises);
        this.response.payments = this.response.payments.sort((a, b) =>
          a.paymentDate > b.paymentDate ? -1 : 1
        );
      }
    },
    async generateInvoice() {
      this.loading.invoice = true;
      let salesOrderClient = new SalesOrderClient(this.rightDrawer.data.id);
      await salesOrderClient.generateInvoice();
      this.loading.invoice = false;
    },
    /* Misc */
    viewResource(type, value) {
      switch (type) {
        case "phone":
          window.open(`tel:${value}`);
          break;
        case "email":
          window.open(`mailto:${value}`);
          break;
        case "url":
          window.open(value, "_blank");
          break;
      }
    },
    loadAddress() {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
          this.deliveryAddress
        )}`,
        "_blank"
      );
    },
    firstNameWithLastInitialFromValue(name) {
      if (name.length > 0) {
        let capitalizedName = name
          .split(" ")
          .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`);
        return `${capitalizedName[0]} ${capitalizedName[1].charAt(0)}.`;
      } else {
        return "[Name Not Set]";
      }
    },
  },
  computed: {
    ...mapGetters([
      "getAppName",
      "getUser",
      "getRightDrawer",
      "getOrderStatus",
      "getPaymentTerms",
      "getPaymentTypes",
      "getCreditTypes",
    ]),
    rightDrawer: {
      get: function () {
        return this.getRightDrawer;
      },
      set: function (value) {
        this._rightDrawer({
          ...this.getRightDrawer,
          active: value,
        });
      },
    },
    licenseExpired() {
      let expirationDate = "",
        daysUntilExpired = 0;
      if (this.getRightDrawer.data.accountData.license.expiration) {
        expirationDate = moment(
          this.getRightDrawer.data.accountData.license.expiration.seconds * 1000
        ).format("MM/DD/YYYY");
        daysUntilExpired = moment().diff(expirationDate, "days");
      }

      return daysUntilExpired > 0 ? true : false;
    },
    deliveryAddress() {
      if (!this.getRightDrawer.data?.location) return null;
      const unitNumber = this.getRightDrawer.data?.location?.shipping
        ?.unitNumber
        ? `${this.getRightDrawer.data.location.shipping.unitNumber} `
        : "";

      return `${unitNumber}${
        this.getRightDrawer.data.location.shipping.street.number
      } ${this.$options.filters.capitalize(
        this.getRightDrawer.data.location.shipping.street.name
      )}, ${this.$options.filters.capitalize(
        this.getRightDrawer.data.location.shipping.city
      )} ${this.getRightDrawer.data.location.shipping.state.abbreviation.toUpperCase()}, ${
        this.getRightDrawer.data.location.shipping.zipCode
      }`;
    },
    totalSaved() {
      let lineItems = this.response.lineItems.length
        ? this.response.lineItems
        : this.rightDrawer?.data?.lineItems ?? [];
      return lineItems
        .filter((item) => item.amount < item.price.normal)
        .reduce((sum, item) => {
          let discountAmount = item.price.normal - item.amount;
          return sum + discountAmount * item.qty;
        }, 0);
    },
    exciseTax() {
      if (this.response.order?.balance.excise) {
        return this.response.order?.balance.excise;
      } else {
        let lineItems = this.response.lineItems.length
          ? this.response.lineItems
          : this.rightDrawer?.data?.lineItems ?? [];
        return lineItems.reduce((sum, item) => {
          let itemQTY = item.singles + item.cases * item.unitsPerCase;
          return sum + (item.tax ?? 0) * itemQTY;
        }, 0);
      }
    },
    totalCreditsApplied() {
      return this.response.payments
        .filter(
          (payment) =>
            (payment.flags === null ||
              (payment.flags && payment.flags.reversed === false)) &&
            payment.fullPaymentType.value === "promotionCredit"
        )
        .reduce((a, b) => a + b.amount, 0);
    },
    totalCaseDiscounts() {
      let lineItems = this.response.lineItems.length
        ? this.response.lineItems
        : this.rightDrawer?.data?.lineItems ?? [];
      return lineItems
        .filter((item) => item.discount?.value > 0)
        .reduce((sum, item) => {
          return sum + item.discount.value * item.qty;
        }, 0);
    },
    groupedLineItems() {
      return (
        this.rightDrawer?.data?.lineItems?.slice().sort((a, b) => {
          if (a.categoryName > b.categoryName) {
            return 1;
          } else if (a.categoryName === b.categoryName && a.name > b.name) {
            return 1;
          } else if (a.categoryName === b.categoryName && a.name === b.name) {
            return 0;
          } else if (a.categoryName === b.categoryName && a.name < b.name) {
            return -1;
          } else if (a.categoryName < b.categoryName) {
            return -1;
          }
        }) ?? []
      );
    },
    promoUnitQty() {
      if (!this.rightDrawer?.data) return 0;
      return this.rightDrawer.data.lineItems
        .filter((item) => item.promo)
        .reduce((sum, item) => sum + item.qty, 0);
    },
    promoUnitValue() {
      if (!this.rightDrawer?.data) return 0;
      return this.rightDrawer.data.lineItems
        .filter((item) => item.promo)
        .reduce((sum, item) => sum + item.qty * item.price.normal, 0);
    },
    promoUnitPercentage() {
      if (!this.rightDrawer?.data) return 0;
      let promo = this.rightDrawer.data.lineItems
        .filter((item) => item.promo)
        .reduce((sum, item) => sum + item.qty * item.price.normal, 0);
      let sale = this.rightDrawer.data.lineItems
        .filter((item) => !item.promo)
        .reduce((sum, item) => sum + item.qty * item.price.sale, 0);

      return (promo / sale) * 100;
    },
  },
};
</script>
